import { apiRequest } from '../config/api';

type CdnFilesResponse = {
  files: Array<{ name: string }>;
};

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp'];

function isImageFile(name: string): boolean {
  const lower = name.toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export type MapMarkerOption = {
  value: string;
  label: string;
};

/** Root CDN icons plus optional regional folder; kept separate for optgroups in the UI. */
export type MapMarkerCdnGrouped = {
  shared: MapMarkerOption[];
  regional: MapMarkerOption[];
};

function sortByValue(a: MapMarkerOption, b: MapMarkerOption): number {
  return a.value.localeCompare(b.value);
}

/**
 * Lists map marker images: always the map-markers CDN root (shared/default icons), plus
 * map-markers/{regionSlug}/ when a region slug is provided.
 * Values stored in map_marker: root = bare filename; regional = "{slug}/{filename}".
 */
export async function fetchMapMarkerCdnOptions(
  token: string,
  regionSlug: string | undefined
): Promise<MapMarkerCdnGrouped> {
  const root = await apiRequest<CdnFilesResponse>('/admin/cdn/files?folder=map-markers', token);

  const shared: MapMarkerOption[] = [];
  for (const file of root.files || []) {
    if (!isImageFile(file.name)) continue;
    shared.push({
      value: file.name,
      label: file.name,
    });
  }
  shared.sort(sortByValue);

  const regional: MapMarkerOption[] = [];
  const slug = regionSlug?.trim();
  if (slug) {
    const folderParam = `map-markers/${slug}/`;
    const regionalRes = await apiRequest<CdnFilesResponse>(
      `/admin/cdn/files?folder=${encodeURIComponent(folderParam)}`,
      token
    );
    for (const file of regionalRes.files || []) {
      if (!isImageFile(file.name)) continue;
      regional.push({
        value: `${slug}/${file.name}`,
        label: file.name,
      });
    }
    regional.sort(sortByValue);
  }

  return { shared, regional };
}

/** Flat list for “current value not in list” checks. */
export function flattenMapMarkerOptions(grouped: MapMarkerCdnGrouped): MapMarkerOption[] {
  return [...grouped.shared, ...grouped.regional];
}
